import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Connecting an account to a clinic or employer.
 *
 * One component, two placements, because the states are identical and only the
 * chrome differs:
 *
 *   variant="banner" — floats after a QR redemption resolves. Appears only when
 *                      there is something to say.
 *   variant="card"   — sits on a settings page for staff who never saw a QR and
 *                      are typing the code from a poster or an email.
 *
 * The middle state is the one that matters: a staff code restricted to an
 * employer's domains answers with "we emailed you six digits" rather than a
 * seat. Without somewhere to type them, that path dead-ends.
 */
export default function ReferralFlow({ variant = 'banner' }) {
  const { referral, redeemReferralCode, dismissReferral } = useAuth();
  const [code, setCode] = useState('');
  const [digits, setDigits] = useState('');
  const [busy, setBusy] = useState(false);
  const digitsRef = useRef(null);

  const needsCode = referral?.status === 'verify';

  // Put the cursor where the person is being asked to type.
  useEffect(() => {
    if (needsCode) digitsRef.current?.focus();
  }, [needsCode]);

  if (variant === 'banner' && !referral) return null;

  const submitCode = async (e) => {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    try { await redeemReferralCode(code); } finally { setBusy(false); }
  };

  const submitDigits = async (e) => {
    e.preventDefault();
    if (digits.length !== 6 || busy) return;
    setBusy(true);
    try {
      const r = await redeemReferralCode(referral.code, digits);
      if (r?.status !== 'ok') setDigits('');
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (busy) return;
    setBusy(true);
    setDigits('');
    try { await redeemReferralCode(referral.code); } finally { setBusy(false); }
  };

  // ── the three states ──────────────────────────────────────────────────────
  let body = null;

  if (needsCode) {
    body = (
      <form onSubmit={submitDigits}>
        <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Check your work email</p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          We sent a 6-digit code to <span className="font-medium text-slate-700">{referral.email}</span>.
          It expires in {referral.expiresInMinutes} minutes.
        </p>
        <div className="flex gap-2 mt-3">
          <input
            ref={digitsRef}
            value={digits}
            onChange={(e) => setDigits(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            aria-label="6-digit verification code"
            className="flex-1 min-w-0 border border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 text-lg tracking-[0.3em] font-semibold text-slate-800 text-center focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <button
            type="submit"
            disabled={digits.length !== 6 || busy}
            className="bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40 hover:bg-cyan-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
          >
            {busy ? '…' : 'Confirm'}
          </button>
        </div>
        {referral.error && <p className="text-sm text-rose-600 mt-2">{referral.error}</p>}
        <button
          type="button" onClick={resend} disabled={busy}
          className="text-sm text-cyan-700 hover:text-cyan-800 mt-2 underline underline-offset-2 disabled:opacity-40"
        >
          Send a new code
        </button>
      </form>
    );
  } else if (referral?.status === 'ok') {
    const { organization, granted, tier, expiresAt, coveredByEmployer, discounted, kind,
            supersededAttribution } = referral;
    body = (
      <>
        <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">You’re connected to {organization}.</p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          {coveredByEmployer
            ? `They’ve covered your ${tier === 'active' ? 'full' : 'starter'} access — you won’t be charged.`
            : discounted
              ? 'Your employer’s rate is applied at checkout.'
              : granted
                ? `You have ${tier === 'active' ? 'full' : 'starter'} access${expiresAt ? ` until ${new Date(expiresAt).toLocaleDateString()}` : ''}.`
                : kind === 'staff'
                  // A staff member at a referral-only facility is linked and nothing
                  // more. Without this they were handed the patient line — told to
                  // upload the results they were "sent home with", moments after
                  // proving they work there.
                  ? 'Your account is linked. This code doesn’t change your plan on its own.'
                  : 'Upload the results you were sent home with and we’ll explain them in your language.'}
        </p>
        {supersededAttribution && (
          // Their earlier clinic link is gone, and they would otherwise just
          // notice it missing. A seat replaces an attribution rather than
          // sitting alongside it, because a person links to one organisation.
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
            This replaces the clinic code you used before — an account links to one organisation at a time.
          </p>
        )}
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1.5">
          {organization} can see how many people used this code, never who or what their results say.
        </p>
      </>
    );
  } else if (referral?.status === 'error') {
    // The reason leads, not the headline. This used to read "We couldn't
    // connect that code" in bold with the actionable line muted underneath —
    // which is backwards, and reported as an error with nothing below it. The
    // sentence that says what to do next is the content; the generic line is
    // only a fallback for the rare case where nothing more specific exists.
    body = (
      <>
        <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">
          {referral.message || 'We couldn’t connect that code'}
        </p>
        {referral.reason && (
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{referral.reason}</p>
        )}
      </>
    );
  }

  // ── code entry, card variant only ─────────────────────────────────────────
  const entry = variant === 'card' && !needsCode && referral?.status !== 'ok' && (
    <form onSubmit={submitCode} className={body ? 'mt-4 pt-4 border-t border-slate-200 dark:border-slate-700' : ''}>
      <label htmlFor="referral-code" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
        Code from your clinic or employer
      </label>
      <div className="flex gap-2">
        <input
          id="referral-code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="RIVERSIDESTAFF"
          autoComplete="off"
          className="flex-1 min-w-0 border border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 text-sm font-medium tracking-wide text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />
        <button
          type="submit"
          disabled={!code.trim() || busy}
          className="bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40 hover:bg-cyan-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        >
          {busy ? '…' : 'Connect'}
        </button>
      </div>
      <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
        Staff codes are confirmed by a code sent to your work email.
      </p>
    </form>
  );

  const inner = (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-cyan-50 dark:bg-cyan-900/30 flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          {referral?.status === 'ok'
            ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            : <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />}
        </svg>
      </div>
      <div className="min-w-0 flex-1">{body}{entry}</div>
      {variant === 'banner' && (
        <button
          onClick={dismissReferral}
          aria-label="Dismiss"
          className="text-slate-400 hover:text-slate-600 transition-colors shrink-0 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );

  if (variant === 'card') {
    return <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-5">{inner}</div>;
  }

  return (
    <div role="status" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[min(30rem,calc(100%-2rem))]">
      <div className="rounded-2xl border border-cyan-200 dark:border-cyan-800 bg-white dark:bg-slate-800 shadow-lg px-4 py-3">{inner}</div>
    </div>
  );
}
